import {lazy, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {usePermissions} from './usePermissions'

// Creates a lazy loader with a minimum load time of 300 seconds to allow the suspense fallback to show long enough to make sense
export const minimumDelayLazyLoader = (filename) =>
  lazy(() =>
    Promise.all([
      import(`../components/SettingsTabs/${filename}/index.jsx`),
      new Promise((resolve) => {
        setTimeout(resolve, 300)
      }),
    ]).then(([moduleExports]) => moduleExports),
  )

const BankingTab = minimumDelayLazyLoader('BankingTab')
const RewardsTab = minimumDelayLazyLoader('RewardsTab')

export const useSettingsTabs = () => {
  const {t} = useTranslation('paymentsOnDemand')
  const {data, isLoading} = usePermissions()

  const {
    screenAccess: {settingsScreen: settingsUnlocked} = {},
    settingsTabs: {
      bankingTab: bankingTabEnabled,
      rewardsTab: rewardsTabEnabled,
    } = {},
  } = data || {}

  // If settings are not unlocked yet then the product will not contribute any tabs to the settings page
  const settingsTabs = useMemo(() => {
    if (!settingsUnlocked) {
      return []
    }

    return [
      bankingTabEnabled && {
        tabName: 'banking',
        label: t('paymentsOnDemand:settingsTabs.bankingTab.label'),
        component: <BankingTab />,
      },
      rewardsTabEnabled && {
        tabName: 'rewards',
        label: t('paymentsOnDemand:settingsTabs.rewardsTab.label'),
        component: <RewardsTab />,
      },
    ].filter(Boolean)
  }, [settingsUnlocked, t, bankingTabEnabled, rewardsTabEnabled])

  return {
    isLoading,
    settingsTabs,
  }
}
